import React, { useState } from "react";
import { Link } from "react-router-dom";
import { orderItemThumbnailSrc, orderLineDisplayLabel } from "../utils/orderLineMedia";
import { formatMoney } from "../utils/money";
import { StatusPill, formatWhen, normalizeStatus } from "./orderKit";

import { t } from "../i18n";
function OrderCard({ order: o }) {
  const [expanded, setExpanded] = useState(false);
  const items = o.items || [];
  const status = normalizeStatus(o.status);
  const units = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  const summary =
    items
      .slice(0, 3)
      .map((i) => `${i.quantity}× ${orderLineDisplayLabel(i)}`)
      .join(", ") + (items.length > 3 ? ` ${t("order.n_more", { n: items.length - 3 })}` : "");

  let action = null;
  if (status === "CREATED") {
    action = (
      <Link className="button-link button-link--primary" to={`/fulfill?order=${o.id}`}>
        <i className="fa-solid fa-clipboard-check" aria-hidden="true" /> {t("order.fulfil")}
      </Link>
    );
  } else if (status === "READY") {
    action = (
      <Link className="button-link button-link--primary" to={`/ready-orders?select=${o.id}`}>
        <i className="fa-solid fa-route" aria-hidden="true" /> {t("order.plan_route")}
      </Link>
    );
  }

  return (
    <article className={`order-card${o.directDeliveryOnly ? " order-card--direct" : ""}`}>
      <div className="order-card__head">
        <div className="order-card__title">
          <span className="order-card__id">{t("order.title", { id: o.id })}</span>
          <StatusPill status={o.status} />
          {o.directDeliveryOnly ? <span className="rom-direct-chip" style={{ margin: 0 }}>{t("order.direct_delivery")}</span> : null}
        </div>
        <div className="order-card__total">{t("common.amount_etb", { amount: formatMoney(o.total) })}</div>
      </div>

      <div className="order-card__meta">
        <span>
          <i className="fa-regular fa-clock" aria-hidden="true" /> {formatWhen(o.createdAt)}
        </span>
        {o.clientPhone ? (
          <a href={`tel:+${String(o.clientPhone).replace(/^\+/, "")}`}>
            <i className="fa-solid fa-phone" aria-hidden="true" /> +{String(o.clientPhone).replace(/^\+/, "")}
          </a>
        ) : null}
        {o.deliveryAddress ? (
          <span className="order-card__address" title={o.deliveryAddress}>
            <i className="fa-solid fa-location-dot" aria-hidden="true" /> {o.deliveryAddress}
          </span>
        ) : null}
      </div>

      {items.length > 0 ? (
        <button
          type="button"
          className="order-card__items-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="thumb-stack">
            {items.slice(0, 4).map((item) => (
              <img key={item.id} src={orderItemThumbnailSrc(item.imageUrl)} alt="" loading="lazy" />
            ))}
          </span>
          <span className="order-card__summary">
            <strong>{units === 1 ? t("order.one_item") : t("order.n_items", { n: units })}</strong>{" "}
            · {summary}
          </span>
          <i className={`fa-solid ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`} aria-hidden="true" />
        </button>
      ) : (
        <div className="muted" style={{ fontSize: 13 }}>{t("order.no_items")}</div>
      )}

      {expanded ? (
        <ul className="order-list-items order-card__items">
          {items.map((item) => (
            <li key={item.id} className="order-list-item-row">
              <img
                className="order-list-item-thumb"
                src={orderItemThumbnailSrc(item.imageUrl)}
                alt=""
                width={52}
                height={52}
                loading="lazy"
                decoding="async"
              />
              <div className="order-list-item-text">
                <div className="order-list-item-title">
                  {item.quantity}× {orderLineDisplayLabel(item)}
                </div>
                {item.sku && orderLineDisplayLabel(item) !== String(item.sku) ? (
                  <div className="muted order-list-item-sku">{item.sku}</div>
                ) : null}
              </div>
              {item.unitPrice != null ? (
                <div className="muted" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                  {t("common.amount_etb", { amount: formatMoney(item.unitPrice) })}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {action ? <div className="order-card__actions">{action}</div> : null}
    </article>
  );
}

export function OrderList({ orders }) {
  if (!orders?.length) {
    return <div className="muted">{t("order.none_yet")}</div>;
  }
  return (
    <div className="order-cards">
      {orders.map((o) => (
        <OrderCard key={o.id} order={o} />
      ))}
    </div>
  );
}
